import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { APP_NAME, PAGE_ID } from "@/config";
import { api, authExpiredEvent, authStorageKey, jsonBody } from "@/lib/api";
import type { Profile } from "@/types";

type Session = { token: string; profile: Profile };
type AuthValue = {
  session: Session | null;
  requestCode(email: string): Promise<void>;
  verifyCode(email: string, code: string): Promise<void>;
  signInWithGoogle(): Promise<void>;
  deleteAccount(): Promise<void>;
  signOut(): Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

function restore(): Session | null {
  try {
    const value = JSON.parse(localStorage.getItem(authStorageKey) || "null") as Session | null;
    return value?.token && Number(value.profile?.PageId) === PAGE_ID ? value : null;
  } catch { return null; }
}

function sessionFrom(data: Record<string, unknown>): Session {
  const token = String(data.jwtToken || data.token || "");
  const profile = (data.profile || data.apiData) as Profile;
  if (!token || !profile || Number(profile.PageId) !== PAGE_ID) throw new Error("ဤအက်ပ်အတွက် အကောင့်ဝင်ခြင်း မမှန်ကန်ပါ။");
  return { token, profile };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(restore);
  useEffect(() => {
    const expire = () => setSession(null);
    window.addEventListener(authExpiredEvent, expire);
    return () => window.removeEventListener(authExpiredEvent, expire);
  }, []);
  const store = useCallback((data: Record<string, unknown>) => {
    const value = sessionFrom(data);
    localStorage.setItem(authStorageKey, JSON.stringify(value));
    setSession(value);
  }, []);

  const requestCode = useCallback(async (email: string) => {
    await api("/farmer/api/auth/email-login/request", { method: "POST", ...jsonBody({ email, pageId: PAGE_ID, pageTitle: APP_NAME }) });
  }, []);
  const verifyCode = useCallback(async (email: string, code: string) => {
    const data = await api<Record<string, unknown>>("/farmer/api/auth/email-login/verify", { method: "POST", ...jsonBody({ email, code, pageId: PAGE_ID, appName: APP_NAME }) });
    store(data);
  }, [store]);
  const signInWithGoogle = useCallback(async () => {
    const { signInWithFarmerGoogle } = await import("@/lib/firebase");
    store(await signInWithFarmerGoogle());
  }, [store]);
  const deleteAccount = useCallback(async () => {
    const result = await api<{ ok?: boolean; deleted?: boolean }>("/farmer/api/account", {
      method: "DELETE",
      ...jsonBody({ confirmation: "DELETE_FARMER_ACCOUNT" }),
    });
    if (!result?.ok || !result.deleted) throw new Error("Account deletion was not confirmed by the server");
    localStorage.removeItem(authStorageKey);
    setSession(null);
  }, []);
  const signOut = useCallback(async () => {
    try { await api("/farmer/api/auth/logout", { method: "POST" }); } catch { /* local logout still succeeds */ }
    localStorage.removeItem(authStorageKey);
    setSession(null);
  }, []);
  const value = useMemo(() => ({ session, requestCode, verifyCode, signInWithGoogle, deleteAccount, signOut }), [session, requestCode, verifyCode, signInWithGoogle, deleteAccount, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be inside AuthProvider");
  return value;
}
